import React from 'react'
import moment from 'moment'
import { Check, ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import { Button as UIButton } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import Currency from '../../currency'
import PaymentTerm from '../orders/payment_term'
import store from '../../../state/store'

const PAYMENT_TERM_LABELS = {
  monthly_credit_card: 'Monthly Subscription (Automatic card payments)',
  monthly_subscription_manual: 'Monthly Subscription (Manual Payments)'
}

const PAYMENT_TERM_SHORT = {
  monthly_credit_card: 'Credit Card',
  monthly_subscription_manual: 'Manual Payment'
}

const paymentTermLabel = (key) => PAYMENT_TERM_LABELS[key] || key
const paymentTermShort = (key) => PAYMENT_TERM_SHORT[key] || key

class AddCoachbitSubscription extends React.Component {

  constructor(props) {
    super(props)

    const data = props.data || {}
    const order = data.order || null
    const pricing = data.pricing || {}
    const defaultPaymentTerm = (pricing.supported_payment_terms || [])[0] || 'monthly_credit_card'

    const orderIsTrial = !!(order && order.trial_months && order.trial_months > 0)

    this.state = {
      checkin_days: order ? String(order.checkin_days || '') : '',
      payment_term: order ? (order.payment_term || defaultPaymentTerm) : defaultPaymentTerm,
      selected_action_key: 'place',
      dropdownOpen: false,
      show_advanced: orderIsTrial,
      enable_trial: orderIsTrial,
      trial_months: (order && order.trial_months) || 1
    }
  }

  onDropdownOpenChange = (open) => {
    this.setState({dropdownOpen: open})
  }

  onCheckinDaysChange = (value) => {
    this.setState({checkin_days: value})
  }

  onPaymentTermChange = (value) => {
    this.setState({payment_term: value})
  }

  onSelectAction = (key) => {
    this.setState({selected_action_key: key})
  }

  onProceed = (e) => {
    e.preventDefault()
    const actions = this.buildFinaliseActions()
    const action = actions.find((a) => a.key === this.state.selected_action_key) || actions[0]
    this.setState({dropdownOpen: false})
    if (action) action.handler(e)
  }

  basePayload = () => {
    const order = (this.props.data && this.props.data.order) || null
    const payload = {
      enquiry_learner_id: this.props.enquiryLearnerId,
      checkin_days: parseInt(this.state.checkin_days, 10),
      payment_term: this.state.payment_term,
      currency_code: (this.props.data && this.props.data.pricing && this.props.data.pricing.currency_code) || 'ZAR',
      trial_months: this.state.enable_trial ? this.state.trial_months : 0
    }
    if (order) {
      payload.coachbit_order_id = order.id
    }
    return payload
  }

  onSaveChanges = (e) => {
    e.preventDefault()
    const order = this.props.data && this.props.data.order
    if (!order) return

    store.emit('coachbit_order:update', this.props.enquiryId, order.id, {
      checkin_days: parseInt(this.state.checkin_days, 10),
      payment_term: this.state.payment_term,
      trial_months: this.state.enable_trial ? this.state.trial_months : 0
    })

    this.props.onDone()
  }

  onPlace = (e) => {
    e.preventDefault()
    store.emit('coachbit_order:place', this.props.enquiryId, this.basePayload())
    this.props.onDone()
  }

  onActivate = (e) => {
    e.preventDefault()
    store.emit('coachbit_order:activate', this.props.enquiryId, this.basePayload())
    this.props.onDone()
  }

  onToggleAdvanced = () => {
    this.setState({ show_advanced: !this.state.show_advanced })
  }

  onToggleTrial = (e) => {
    const enable = e.target.checked
    const newState = { enable_trial: enable, selected_action_key: 'place' }
    if (enable) {
      newState.checkin_days = '1'
    }
    this.setState(newState)
  }

  onSelectTrialMonths = (value) => {
    this.setState({ trial_months: parseInt(value, 10) })
  }

  onAddToOrder = (orderId) => (e) => {
    e.preventDefault()
    const payload = Object.assign({}, this.basePayload(), {
      order_id: orderId
    })
    store.emit('coachbit_order:add_to_order', this.props.enquiryId, payload)
    this.props.onDone()
  }

  onCancel = (e) => {
    e.preventDefault()
    this.props.onDone()
  }

  buildFinaliseActions = () => {
    const data = this.props.data || {}
    const order = data.order || null
    const existing = data.existing_learner || {}
    const existingOrders = data.existing_orders || []
    const pricing = data.pricing || {}
    const currencyCode = pricing.currency_code || 'ZAR'
    const actions = []

    const isPlaced = !!order && order.workflow_state === 'placed'

    if (isPlaced) {
      actions.push({
        key: 'save',
        label: 'Save Changes',
        handler: this.onSaveChanges
      })
      return actions
    }

    const checkinDays = parseInt(this.state.checkin_days, 10)
    const priceValue = (pricing.prices || {})[checkinDays] || null

    if (!this.state.enable_trial) {
      actions.push({
        key: 'place',
        label: 'Create New Order',
        description: `Coachbit Order${existing.first_name ? ` for ${existing.first_name}` : ''}`,
        paymentTerm: this.state.payment_term,
        amount: priceValue,
        currency: currencyCode,
        handler: this.onPlace
      })
    }

    const matchingOrders = existingOrders.filter((o) =>
      o.payment_term === this.state.payment_term &&
      o.currency_code === currencyCode
    )

    matchingOrders.forEach((o) => {
      const addedValue = this.state.enable_trial ? 0 : priceValue
      const newTotal = (Number.isFinite(o.total) && Number.isFinite(addedValue))
        ? o.total + addedValue
        : null

      actions.push({
        key: `add_${o.id}`,
        label: this.state.enable_trial ? `Add Trial to Order #${o.id}` : `Add to Order #${o.id}`,
        description: this.state.enable_trial ? `${o.description} — trial item added free` : o.description,
        paymentTerm: o.payment_term,
        parts: { existing: o.total, added: addedValue, total: newTotal },
        currency: o.currency_code,
        handler: this.onAddToOrder(o.id)
      })
    })

    return actions
  }

  render() {
    const data = this.props.data || {}
    const order = data.order || null
    const pricing = data.pricing || { prices: {}, supported_payment_terms: [], checkin_days_options: [1,2,3,4,5], currency_code: 'ZAR' }

    const isEdit = !!order
    const isEditable = isEdit ? order.editable : true
    const trialLocked = !!(order && order.workflow_state !== 'placing')
    const orderIsTrial = !!(order && order.trial_months > 0)
    const trialAvailable = !!data.has_coachbit_trial_available
    const trialUnavailable = !trialLocked && !trialAvailable && !orderIsTrial

    const checkinDays = parseInt(this.state.checkin_days, 10)
    const priceValue = (pricing.prices || {})[checkinDays] || null

    const inputsReady = !!(
      this.state.checkin_days &&
      this.state.payment_term &&
      isEditable
    )

    const actions = inputsReady ? this.buildFinaliseActions() : []

    return (
      <div>
        <div className="mb-3">
          <div className="grid grid-cols-1 lg:grid-cols-3 lg:gap-8">
            <div className="lg:col-span-2">
              <div className="grid grid-cols-[200px_1fr] gap-x-4 gap-y-4 items-start">
                {isEdit && (
                  <React.Fragment>
                    <span className="text-sm font-medium text-muted-foreground">Status:</span>
                    <span className="flex items-center gap-2">
                      {order.paid ? (
                        <Badge variant="success" className="capitalize">Paid</Badge>
                      ) : (
                        <Badge variant="secondary" className="capitalize">{order.workflow_state}</Badge>
                      )}
                      {this.state.enable_trial && (
                        <Badge variant="secondary" className="capitalize">Trial</Badge>
                      )}
                    </span>
                  </React.Fragment>
                )}

                <span className="text-sm font-medium text-muted-foreground">Type of Coaching:</span>
                <span className="text-sm">Video</span>

                <span className="text-sm font-medium text-muted-foreground">Check-in length:</span>
                <span className="text-sm">15 min per check-in</span>

                <span className="text-sm font-medium text-muted-foreground">Number of check-in days:</span>
                <div className="inline-flex w-fit gap-1 rounded-full bg-muted p-1">
                  {(pricing.checkin_days_options || [1,2,3,4,5]).map((n) => {
                    const active = parseInt(this.state.checkin_days) === n
                    return (
                      <button
                        key={n}
                        type="button"
                        disabled={!isEditable || (this.state.enable_trial && n !== 1)}
                        onClick={() => this.onCheckinDaysChange(n)}
                        className={cn(
                          'inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50',
                          active ? 'bg-foreground text-background shadow-sm' : 'text-muted-foreground hover:text-foreground'
                        )}
                      >
                        <Check className={cn('size-3.5 transition-opacity', active ? 'opacity-100' : 'opacity-0')} />
                        {n}
                      </button>
                    )
                  })}
                </div>

                <span className="text-sm font-medium text-muted-foreground">Payment Term:</span>
                <div className="flex w-fit max-w-full flex-col items-start gap-1 rounded-[20px] bg-muted p-1">
                  {(pricing.supported_payment_terms || []).map((term) => {
                    const active = this.state.payment_term === term
                    return (
                      <button
                        key={term}
                        type="button"
                        disabled={!isEditable}
                        onClick={() => this.onPaymentTermChange(term)}
                        className={cn(
                          'inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50',
                          active ? 'bg-foreground text-background shadow-sm' : 'text-muted-foreground hover:text-foreground'
                        )}
                      >
                        <Check className={cn('size-3.5 transition-opacity', active ? 'opacity-100' : 'opacity-0')} />
                        {paymentTermLabel(term)}
                      </button>
                    )
                  })}
                </div>

                <React.Fragment>
                  <span></span>
                  <div>
                    <button
                      type="button"
                      className="p-0 bg-transparent border-0 text-sm text-muted-foreground cursor-pointer hover:underline"
                      onClick={this.onToggleAdvanced}
                    >
                      {this.state.show_advanced ? 'Hide advanced options' : 'Advanced options'}
                    </button>

                    {this.state.show_advanced && trialLocked && (
                      <div className="mt-2">
                        <label className="flex items-center m-0 text-[13px]">
                          <input
                            type="checkbox"
                            checked={this.state.enable_trial}
                            disabled
                          />
                          <span className="text-muted-foreground ml-2">Activate Coachbit trial</span>
                        </label>
                        <div className="mt-3 rounded-[10px] border border-info/30 bg-info/10 p-4">
                          <div className="text-[13px] leading-relaxed">
                            {order.paid
                              ? 'This order has been paid and can no longer be edited.'
                              : "The trial setting can't be changed once an order is placed. To change it, cancel this order and create a new one."}
                          </div>
                        </div>
                      </div>
                    )}

                    {this.state.show_advanced && trialUnavailable && (
                      <div className="mt-2">
                        <label className="flex items-center m-0 text-[13px]">
                          <input
                            type="checkbox"
                            checked={false}
                            disabled
                          />
                          <span className="text-muted-foreground ml-2">Activate Coachbit trial</span>
                        </label>
                        <div className="mt-3 rounded-[10px] border border-info/30 bg-info/10 p-4">
                          <div className="text-[13px] leading-relaxed">
                            This customer has already used their Coachbit trial. Only one trial is allowed per customer.
                          </div>
                        </div>
                      </div>
                    )}

                    {this.state.show_advanced && !trialLocked && !trialUnavailable && (
                      <div className="mt-2">
                        <label className="flex items-center cursor-pointer m-0 text-[13px]">
                          <input
                            type="checkbox"
                            checked={this.state.enable_trial}
                            onChange={this.onToggleTrial}
                          />
                          <span className="text-foreground ml-2">Activate Coachbit trial</span>
                        </label>

                        {this.state.enable_trial && (
                          <React.Fragment>
                            <div className="mt-2 inline-flex w-fit gap-1 rounded-full bg-muted p-1">
                              {[1, 3].map((n) => {
                                const active = this.state.trial_months === n
                                return (
                                  <button
                                    key={n}
                                    type="button"
                                    onClick={() => this.onSelectTrialMonths(n)}
                                    className={cn(
                                      'inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-sm font-medium transition-colors',
                                      active ? 'bg-foreground text-background shadow-sm' : 'text-muted-foreground hover:text-foreground'
                                    )}
                                  >
                                    <Check className={cn('size-3.5 transition-opacity', active ? 'opacity-100' : 'opacity-0')} />
                                    {n} month{n > 1 ? 's' : ''}
                                  </button>
                                )
                              })}
                            </div>

                            <div className="mt-3 rounded-[10px] border border-info/30 bg-info/10 p-4">
                              <div className="text-[13px] leading-relaxed">
                                First {this.state.trial_months} month{this.state.trial_months > 1 ? 's are' : ' is'} free. Then{' '}
                                {priceValue != null
                                  ? <Currency value={priceValue} currency={pricing.currency_code || 'ZAR'} placeholder='—' />
                                  : '—'}
                                /month from {moment().add(this.state.trial_months, 'month').format('DD MMM YYYY')}. If unpaid for 7 days after that, the subscription is cancelled automatically.
                              </div>
                              <div className="mt-3 text-[13px] leading-relaxed">
                                You can either start the trial on its own now, or use Finalise to combine it with an existing order.
                              </div>
                              <div className="mt-3 flex justify-end">
                                <UIButton
                                  size="sm"
                                  disabled={!inputsReady}
                                  onClick={this.onActivate}
                                >
                                  Activate now
                                </UIButton>
                              </div>
                            </div>
                          </React.Fragment>
                        )}
                      </div>
                    )}
                  </div>
                </React.Fragment>
              </div>
            </div>

            <div className="mt-4 lg:mt-0">
              <div className="rounded-[10px] border border-border bg-card px-4 py-3">
                <div className="flex items-baseline justify-between">
                  <span className="font-medium">Monthly Total</span>
                  <span className="text-xl font-semibold">
                    {priceValue != null
                      ? <Currency value={priceValue} currency={pricing.currency_code || 'ZAR'} placeholder='—' />
                      : '—'}
                  </span>
                </div>

                <hr className="my-3 border-0 border-t border-border" />

                <div className="flex flex-col gap-1 rounded-md bg-muted p-3">
                  <div className="flex justify-between text-[13px]">
                    <span className="text-muted-foreground">Type of Coaching</span>
                    <span className="font-medium">Video</span>
                  </div>
                  <div className="flex justify-between text-[13px]">
                    <span className="text-muted-foreground">Check-in Length</span>
                    <span className="font-medium">15 min</span>
                  </div>
                  <div className="flex justify-between text-[13px]">
                    <span className="text-muted-foreground">Check-in Days</span>
                    <span className="font-medium">{this.state.checkin_days || '—'}</span>
                  </div>
                  <div className="flex justify-between text-[13px]">
                    <span className="text-muted-foreground">Payment Method</span>
                    <span className="text-right font-medium">
                      {this.state.payment_term ? paymentTermShort(this.state.payment_term) : '—'}
                    </span>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>

        <div className="mt-4 flex items-center gap-2 border-t border-border pt-3">
          <div className="ml-auto flex items-center justify-end gap-2">
            <UIButton variant="outline" size="sm" onClick={this.onCancel}>Cancel</UIButton>
            {actions.length === 1 && actions[0].key === 'save' ? (
              <UIButton
                size="sm"
                disabled={!inputsReady}
                onClick={actions[0].handler}
              >
                Save Changes
              </UIButton>
            ) : (
              <Popover open={this.state.dropdownOpen} onOpenChange={this.onDropdownOpenChange}>
                <PopoverTrigger asChild>
                  <UIButton
                    size="sm"
                    disabled={!inputsReady}
                  >
                    Finalise <ChevronDown className="size-4"/>
                  </UIButton>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-[520px] max-w-[calc(100vw-2rem)] p-3">
                  {actions.length === 0 ? (
                    <div className="px-2 py-4 text-center text-[13px] text-muted-foreground">
                      No existing orders to attach to. Use "Activate now" to create a standalone trial.
                    </div>
                  ) : (
                    <React.Fragment>
                      {actions.map((action) => {
                        const checked = this.state.selected_action_key === action.key
                        return (
                          <div
                            key={action.key}
                            className={cn(
                              'mb-2 flex cursor-pointer items-center gap-3 rounded-md border p-2',
                              checked
                                ? 'border-primary/40 bg-primary/5'
                                : 'border-transparent hover:bg-muted/50'
                            )}
                            onClick={() => this.onSelectAction(action.key)}
                          >
                            <div className="flex w-[40%] items-center gap-2">
                              <input type="radio" checked={checked} readOnly />
                              <label className="m-0 cursor-pointer">{action.label}</label>
                            </div>
                            <div className="flex-1">
                              <div className="text-[13px]">{action.description}</div>
                              <div className="text-xs text-muted-foreground">
                                <PaymentTerm payment_term={action.paymentTerm} />
                              </div>
                            </div>
                            <div className="w-[25%] text-right font-semibold">
                              {action.parts ? (
                                <React.Fragment>
                                  <div className="text-[11px] font-normal text-muted-foreground">
                                    <Currency value={action.parts.existing} currency={action.currency} placeholder="-" />
                                    {' + '}
                                    <Currency value={action.parts.added} currency={action.currency} placeholder="-" />
                                  </div>
                                  <Currency value={action.parts.total} currency={action.currency} placeholder="-" />
                                </React.Fragment>
                              ) : (
                                <Currency value={action.amount} currency={action.currency} placeholder="-" />
                              )}
                            </div>
                          </div>
                        )
                      })}
                      <div className="mt-3 flex justify-end">
                        <UIButton size="sm" onClick={this.onProceed}>Proceed</UIButton>
                      </div>
                    </React.Fragment>
                  )}
                </PopoverContent>
              </Popover>
            )}
          </div>
        </div>
      </div>
    )
  }
}

export default AddCoachbitSubscription
